import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

// Minimal bridge for ZANOR's custom product template and header.
// Cart markup, quantity changes, removals, empty state and dialog lifecycle
// are owned exclusively by the original Shopify Horizon components.
(function(){
  if(window.ZanorCartUI)return;
  const SECTION='cart-drawer-section';
  const root=()=>window.Shopify?.routes?.root||'/';
  const drawer=()=>document.getElementById('cart-drawer');
  let queue=Promise.resolve();
  let adding=0;

  // Loading state shown while an item is being added to an EMPTY cart, so the
  // drawer never shows "Your cart is empty" while the request is in flight.
  (function injectLoadingStyles(){
    if(document.getElementById('zanor-cart-loading-styles'))return;
    const style=document.createElement('style');
    style.id='zanor-cart-loading-styles';
    style.textContent=[
      '#cart-drawer[data-zanor-adding] .cart-drawer--empty{position:relative}',
      '#cart-drawer[data-zanor-adding] .cart-drawer--empty .cart-drawer__content{visibility:hidden}',
      '#cart-drawer[data-zanor-adding] .cart-drawer--empty::before{content:"";position:absolute;left:50%;top:calc(50% - 26px);width:22px;height:22px;margin-left:-11px;border:2px solid rgba(8,8,8,.15);border-top-color:#080808;border-radius:50%;animation:zanor-cart-spin .7s linear infinite}',
      '#cart-drawer[data-zanor-adding] .cart-drawer--empty::after{content:"Adding to bag...";position:absolute;left:0;right:0;top:calc(50% + 8px);text-align:center;font-size:12px;letter-spacing:.04em;color:#080808}',
      '@keyframes zanor-cart-spin{to{transform:rotate(360deg)}}'
    ].join('\n');
    document.head.appendChild(style);
  })();

  function setAdding(on){
    adding=Math.max(0,adding+(on?1:-1));
    const element=drawer();
    if(!element)return;
    if(adding>0)element.setAttribute('data-zanor-adding','');
    else element.removeAttribute('data-zanor-adding');
  }

  function setHeaderCount(qty){
    if(!Number.isFinite(qty)||qty<0)return;
    document.querySelectorAll('.zh__count,.zh__cart-count,[data-zanor-cart-count]').forEach(el=>{
      el.textContent=String(qty);
    });
  }

  async function updateHeaderCount(){
    const response=await fetch(root()+'cart.js',{credentials:'same-origin',cache:'no-store'});
    if(!response.ok)throw new Error('Unable to refresh cart count');
    const cart=await response.json();
    setHeaderCount(Number(cart.item_count));
    return cart;
  }

  function open(){
    const element=drawer();
    if(!element){window.location.href=root()+'cart';return;}
    if(element.hasAttribute('open')&&element.querySelector('dialog')?.open)return;
    if(typeof element.open==='function')element.open();
    else customElements.whenDefined('theme-drawer').then(()=>element.open());
  }

  // Paint the drawer as soon as the add request returns, using the same
  // Horizon morph the native components use. The native handler morphs the
  // same HTML again when the event promise resolves, which is then a no-op.
  // Any failure here is swallowed so the native path still updates the drawer.
  async function paintDrawerEarly(html){
    if(!html)return;
    try{
      const wasEmpty=Boolean(drawer()?.querySelector('.cart-drawer--empty'));
      const renderer=await import('@theme/section-renderer');
      await renderer.morphSection(SECTION,html,{mode:'hydration',injectStylesheet:wasEmpty});
      drawer()?.removeAttribute('data-zanor-adding');
    }catch(error){
      console.warn('[ZANOR] Early cart paint skipped:',error);
    }
  }

  async function performAdd(items){
    if(!Array.isArray(items)||!items.length||items.some(x=>!Number.isInteger(Number(x.id))||Number(x.id)<=0||!Number.isInteger(Number(x.quantity))||Number(x.quantity)<=0)){
      throw new Error('Select a valid product size and quantity.');
    }
    const normalized=items.map(x=>({id:Number(x.id),quantity:Number(x.quantity)}));
    const pending=CartLinesUpdateEvent.createPromise();
    // Native Horizon cart components listen to this event and own their update.
    document.dispatchEvent(new CartLinesUpdateEvent({
      action:'add',context:'product',
      lines:normalized.map(x=>({merchandiseId:String(x.id),quantity:x.quantity})),
      promise:pending.promise
    }));
    try{
      const response=await fetch(root()+'cart/add.js',{
        method:'POST',credentials:'same-origin',
        headers:{'Content-Type':'application/json','Accept':'application/json'},
        body:JSON.stringify({items:normalized,sections:[SECTION],sections_url:root()})
      });
      const result=await response.json().catch(()=>({}));
      if(!response.ok||result.status)throw new Error(result.description||result.message||'Unable to add product to cart.');
      // Show the updated drawer now; the cart confirmation request below runs
      // at the same time instead of holding the drawer back.
      const painted=paintDrawerEarly(result.sections?.[SECTION]);
      const cartResponse=await fetch(root()+'cart.js',{credentials:'same-origin',cache:'no-store'});
      if(!cartResponse.ok)throw new Error('Added to bag; cart confirmation is unavailable. Please check the cart.');
      const cart=await cartResponse.json();
      setHeaderCount(Number(cart.item_count));
      await painted;
      pending.resolve({
        cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail:{items:cart.items,sections:result.sections,source:'zanor-product',didError:false}
      });
      // The native cart-drawer-component[auto-open] opens after it receives
      // the event and handles empty-to-filled cart state itself.
      return result;
    }catch(error){
      pending.reject(error);
      throw error;
    }
  }

  function addItems(items){
    // Open the native Horizon drawer on the original user action, before
    // the network request, with a loading state if the cart is empty.
    setAdding(true);
    open();
    const task=queue.then(()=>performAdd(items));
    queue=task.catch(()=>{});
    queue.then(()=>setAdding(false));
    return task;
  }

  document.addEventListener('click',event=>{
    const trigger=event.target.closest?.('[data-zanor-cart-trigger]');
    if(!trigger)return;
    event.preventDefault();
    open();
  });

  document.addEventListener(StandardEvents.cartLinesUpdate,event=>{
    event.promise?.then(({cart,detail})=>{
      if(detail?.didError)return;
      const count=cart?.totalQuantity;
      if(Number.isFinite(count))setHeaderCount(count);
      else updateHeaderCount().catch(error=>console.warn('[ZANOR] Cart count:',error));
    }).catch(()=>{});
  });
  window.ZanorCartUI={open,addItems,updateHeaderCount};
})();
